/*
 * Copyright (c) 2023 SARL SIGMA POLYNESIA
 */

import { DateTime } from 'luxon'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { calculateProrata } from '../../../lib/prorata.mjs'
import { Code, Dialog, ModalProps, NumberInput, Stack, TextInput, Title } from '@mantine/core'
import { handleValueChange } from '../../../../../client/js/lib/form'
import LabelledValue from '../../LabelledValue'
import Amount from '../../transaction/Amount'

const amountFieldId = 'prorata_amount_field'
const startDateFieldId = 'prorata_startDate_field'
const endDateFieldId = 'prorata_endDate_field'

function ProrataTool (props: ModalProps) {
  const intl = useIntl()
  const { opened, onClose } = props

  const [fields, setFields] = useState({
    amount: null,
    startDate: null,
    endDate: null
  })

  const setField = useCallback((name: string, value: unknown) => {
    setFields((s) => ({ ...s, [name]: value }))
  }, [])

  const handleFieldChange = useCallback((ev: React.ChangeEvent<HTMLInputElement>) => {
    const { target } = ev
    setFields((s) => ({ ...s, [target.name]: target.value }))
  }, [])

  const {
    amountPerDay,
    amountPerDayFormula,
    amountAtProrata,
    amountAtProrataFormula,
    daysAtProrata,
    daysAtProrataFormula
  } = useMemo<{
    amountPerDay?: number,
    amountPerDayFormula?: string,
    amountAtProrata?: number,
    amountAtProrataFormula?: string,
    daysAtProrata?: number,
    daysAtProrataFormula?: string
  }>(() => {
    if (fields.amount && fields.startDate && fields.endDate) {
      const startDt = DateTime.fromISO(fields.startDate)
      const endDt = DateTime.fromISO(fields.endDate)

      if (startDt.isValid && endDt.isValid) {
        return calculateProrata(fields.amount, startDt, endDt)
      }
    }
    return {}
  }, [fields])

  useEffect(() => {
    if (fields.startDate) {
      const dt = DateTime.fromISO(fields.startDate)

      if (dt.isValid) {
        const endDate = dt.endOf('month').toISODate()
        setFields((s) => ({ ...s, endDate }))
      }
    }
  }, [fields.startDate])

  useEffect(() => {
    if (opened) {
      setFields({
        amount: null,
        startDate: null,
        endDate: null
      })
    }
  }, [opened])

  return (
    <Dialog
      onClose={onClose}
      opened={opened}
      withCloseButton
      withinPortal
    >
      <Stack gap="xs">
        <Title order={4}>
          {intl.formatMessage({
            id: 'prorata_calcul',
            defaultMessage: 'Calcul de prorata'
          })}
        </Title>

        <NumberInput
          id={amountFieldId}
          label={intl.formatMessage({
            id: 'amount',
            defaultMessage: 'Montant'
          })}
          name="amount"
          onChange={handleValueChange('amount', setField)}
          required
          thousandSeparator=" "
          value={fields.amount || ''}
        />

        <TextInput
          id={startDateFieldId}
          label={intl.formatMessage({
            id: 'start_date',
            defaultMessage: 'Date de début'
          })}
          name="startDate"
          onChange={handleFieldChange}
          required
          type="date"
          value={fields.startDate || ''}
        />

        <TextInput
          id={endDateFieldId}
          label={intl.formatMessage({
            id: 'end_date',
            defaultMessage: 'Date de fin'
          })}
          name="endDate"
          onChange={handleFieldChange}
          required
          type="date"
          value={fields.endDate || ''}
        />

        <LabelledValue
          fz="sm"
          label={intl.formatMessage({
            id: 'days_at_prorata',
            defaultMessage: 'Nombre de jours au prorata'
          })}
          value={daysAtProrata != null ? daysAtProrata : null}
        />
        <Code>{daysAtProrataFormula}</Code>

        <LabelledValue
          fz="sm"
          label={intl.formatMessage({
            id: 'amount_per_day',
            defaultMessage: 'Montant par jour'
          })}
          value={amountPerDay != null
            ? <Amount inherit span value={amountPerDay} />
            : null}
        />
        <Code>{amountPerDayFormula}</Code>

        <LabelledValue
          fz="sm"
          label={intl.formatMessage({
            id: 'amount_at_prorata',
            defaultMessage: 'Montant au prorata'
          })}
          value={amountAtProrata != null
            ? <Amount inherit span fw="bold" value={amountAtProrata} />
            : null}
        />
        <Code>{amountAtProrataFormula}</Code>
      </Stack>
    </Dialog>
  )
}

export default ProrataTool
